
import packageInfo from "package.json";
import { createAuthenticator } from "~/modules/auth";
import { ContextManager } from "./modules/contextManager/ContextManager";
import { getAccountConfigData } from "./modules/config/AccountConfigDataGetter/getAccountConfigData";
import { setAccountConfigData } from "./modules/config/AccountConfigDataSetter/setAccountConfigData";
import { createClientWrapper as fromModuleCreateClient } from "./modules/client/ClientProvider/createClientWrapper";
import { getPublicConfigData as fromModuleGetPublicConfig } from "./modules/config/PublicConfigProvider/getPublicConfigData";
import { getFeaturesConfig as fromModuleGetFeaturesConfig } from "./modules/config/FeaturesConfigDataGetter/getFeaturesConfig";
import { setLogLevel as fromModuleSetLogLevel } from "~/modules/logger/LoggerFactory/LoggerConfigImpl";
import { validateTokenWrapper as fromModuleValidateToken } from "~/modules/auth/ValidateToken";
import { refreshTokenWrapper as fromModuleRefreshToken } from "~/modules/auth/RefreshToken";
import { exchangeTokenWrapper as fromModuleExchangeToken } from "~/modules/auth/ExchangeToken";
import { getAuthenticationConfig as fromModuleGetAuthenticationConfig } from "~/modules/auth/AuthenticationConfig";
import { getLoginDetails as fromModuleGetLoginDetails } from "~/modules/auth/GetLoginDetails";
import type { DeepPartial } from "./utils/DeepPartial";
import type { ClientOptions } from "./modules/client";
import type { LoggerLevel } from "./modules/logger";
import type { XOR } from "./utils/utilityTypes";
import type { AccountSidOption, RuntimeDomainOption, AccountConfigOptions, AccountConfigData } from "./modules/config";
import type { AuthenticationConfigParams } from "./modules/auth/AuthenticationConfig";
import type { RefreshTokenParams, RefreshTokenResponse } from "./modules/auth/RefreshToken";
import type { ExchangeTokenParams, ExchangeTokenResponse } from "./modules/auth/ExchangeToken";
import type { LoginDetailsParams } from "~/modules/auth/GetLoginDetails";


export type { SetWorkerAttributesOptions } from "~/modules/actions/Supervisor/SetWorkerAttributes/SetWorkerAttributes";
export { SetWorkerAttributes } from "~/modules/actions/Supervisor/SetWorkerAttributes/SetWorkerAttributes";
export type { SetWorkerActivityOptions } from "~/modules/actions/Supervisor/SetWorkerActivity/SetWorkerActivity";
export { SetWorkerActivity } from "~/modules/actions/Supervisor/SetWorkerActivity/SetWorkerActivity";

export type { SetAttributesOptions } from "~/modules/actions/Worker/SetAttributes/SetAttributes";
export { SetAttributes } from "~/modules/actions/Worker/SetAttributes/SetAttributes";

export type { SetCurrentActivityOptions } from "~/modules/actions/Worker/SetCurrentActivity/SetCurrentActivity";
export { SetCurrentActivity } from "~/modules/actions/Worker/SetCurrentActivity/SetCurrentActivity";

export type { SetTaskAttributesOptions } from "~/modules/actions/Worker/SetTaskAttributes/SetTaskAttributes";
export { SetTaskAttributes } from "~/modules/actions/Worker/SetTaskAttributes/SetTaskAttributes";

export { AcceptTask } from "~/modules/actions/Task/AcceptTask/AcceptTask";
export type { AcceptTaskOptions } from "~/modules/actions/Task/AcceptTask/AcceptTask";

export { RejectTask } from "~/modules/actions/Task/RejectTask/RejectTask";

export { CompleteTask } from "~/modules/actions/Task/CompleteTask/CompleteTask";

export { EndTask } from "~/modules/actions/Task/EndTask/EndTask";

export { WrapUpTask } from "~/modules/actions/Task/WrapUpTask/WrapUpTask";

export { CancelVoiceTaskTransfer } from "~/modules/actions/Task/CancelVoiceTaskTransfer/CancelVoiceTaskTransfer";

export type { TransferTaskOptions } from "~/modules/actions/Task/StartVoiceTaskTransfer/StartVoiceTaskTransfer";

export { StartVoiceTaskTransfer } from "~/modules/actions/Task/StartVoiceTaskTransfer/StartVoiceTaskTransfer";

export { GetTaskParticipants } from "~/modules/actions/Task/GetTaskParticipants/GetTaskParticipants";
export type { GetTaskParticipantsResponse } from "~/modules/actions/Task/GetTaskParticipants/GetTaskParticipants";

export { StartOutboundCall } from "~/modules/actions/VoiceChannel/StartOutBoundCall/StartOutBoundCall";
export type { StartOutboundCallOptions } from "~/modules/actions/VoiceChannel/StartOutBoundCall/StartOutBoundCall";

export { StartExternalWarmVoiceTaskTransfer } from "~/modules/actions/VoiceConference/StartExternalWarmVoiceTaskTransfer/StartExternalWarmVoiceTaskTransfer";

export type { HoldParticipantOptions } from "~/modules/actions/VoiceConference/HoldParticipant/HoldParticipant";
export { HoldParticipant } from "~/modules/actions/VoiceConference/HoldParticipant/HoldParticipant";

export { UnholdParticipant } from "~/modules/actions/VoiceConference/UnholdParticipant/UnholdParticipant";

export { KickParticipant } from "~/modules/actions/VoiceConference/KickParticipant/KickParticipant";

export { EndConferenceForAll } from "~/modules/actions/VoiceConference/EndConferenceForAll/EndConferenceForAll";

export { MonitorCall } from "~/modules/actions/VoiceChannel/MonitorCall/MonitorCall";
export type { MonitorCallOptions } from "~/modules/actions/VoiceChannel/MonitorCall/MonitorCall";

export { AddVoiceEventListener } from "~/modules/actions/VoiceChannel/AddVoiceEventListener/AddVoiceEventListener";
export type { AddVoiceEventListenerOptions } from "~/modules/actions/VoiceChannel/AddVoiceEventListener/AddVoiceEventListener";

export { AddParticipant } from "~/modules/actions/VoiceConference/AddParticipant/AddParticipant";

export { AddConversationEventListener } from "~/modules/actions/Conversation/AddConversationEventListener/AddConversationEventListener";

export { LeaveConversationChannel } from "~/modules/actions/Conversation/LeaveConversationChannel/LeaveConversationChannel";

export { PauseConversationChannel } from "~/modules/actions/Conversation/PauseConversationChannel/PauseConversationChannel";

export { StartChannelTransfer } from "~/modules/actions/Conversation/StartChannelTransfer/StartChannelTransfer";
export type { StartChannelTransferOptions } from "~/modules/actions/Conversation/StartChannelTransfer/StartChannelTransfer";

export { GetChannelTransfers } from "~/modules/actions/Conversation/GetChannelTransfers/GetChannelTransfers";
export type {
    GetChannelTransfersOptions,
    ChannelTransfer
} from "~/modules/actions/Conversation/GetChannelTransfers/GetChannelTransfers";

export {
    AddEmailParticipant,
    ParticipantLevel
} from "~/modules/actions/Conversation/AddEmailParticipant/AddEmailParticipant";
export type { AddEmailParticipantOptions } from "~/modules/actions/Conversation/AddEmailParticipant/AddEmailParticipant";

export { RemoveEmailParticipant } from "~/modules/actions/Conversation/RemoveEmailParticipant/RemoveEmailParticipant";

export { StartOutboundEmailTask } from "~/modules/actions/Conversation/StartOutboundEmailTask/StartOutboundEmailTask";
export type {
    StartOutboundEmailTaskOptions,
    StartOutboundEmailTaskResponse
} from "~/modules/actions/Conversation/StartOutboundEmailTask/StartOutboundEmailTask";

export { GetConversationByTask } from "~/modules/actions/Conversation/GetConversationByTask/GetConversationByTask";

export { GetConversationsUser } from "~/modules/actions/Conversation/GetConversationsUser/GetConversationsUser";

export { GetCallByTask } from "~/modules/actions/VoiceChannel/GetCallByTask/GetCallByTask";
export type { GetCallByTaskOptions } from "~/modules/actions/VoiceChannel/GetCallByTask/GetCallByTask";

export { GetPausedConversations } from "~/modules/actions/Conversation/GetPausedConversations/GetPausedConversations";

export { ResumeConversationChannel } from "~/modules/actions/Conversation/ResumeConversationChannel/ResumeConversationChannel";
export type { ResumeConversationChannelResponse } from "~/modules/actions/Conversation/ResumeConversationChannel/ResumeConversationChannel";

export type { Action, TaskReservation } from "~/modules/actions/Action";


export type {
    AuthenticationConfigParams,
    AuthenticationConfigResponse,
    AuthenticationConfig
} from "./modules/auth/AuthenticationConfig";
export type {
    Authenticator,
    AuthenticatorFactory,
    IdpConfig,
    LegacyTokenData,
    SSOTokenData,
    TokenData,
    AppType,
    TokenRefreshResult
} from "./modules/auth";
export type { RefreshTokenParams, RefreshTokenResponse } from "~/modules/auth/RefreshToken";
export type {
    ExchangeTokenParams,
    ExchangeTokenResponse,
    ConsoleExchangeTokenParams,
    SSOExchangeTokenParams
} from "~/modules/auth/ExchangeToken";
export type {
    LoginDetailsParams,
    LoginDetailsResponse,
    SSOLoginDetailsParams,
    ConsoleLoginDetailsParams
} from "./modules/auth/GetLoginDetails";

export type { SessionOptions } from "./modules/session";
export type {
    AccountConfig,
    AccountConfigProvider,
    AccountConfigKeys,
    AccountConfigData,
    IntegrationsConfig,
    OutboundFlows,
    SkillDefinition,
    QueueStatsConfiguration,
    PublicConfig,
    PublicConfigAttributes,
    PublicConfigProvider,
    AccountSidOption,
    RuntimeDomainOption,
    TaskQueue,
    WorkerChannel,
    DefaultQueueStatsConfiguration,
    QueueConfiguration,
    QueueChannelConfiguration,
    FeaturesConfigDataGetter,
    FeaturesConfig,
    FeaturesList,
    AccountConfigDataGetter,
    AccountConfigOptions,
    ChannelConfig,
    CbmAttachments as Attachments,
    DebuggerIntegration,
    FlexUiStatusReport,
    CitrixVoiceVdi
} from "./modules/config";
export type { Logger, LoggerOptions, LoggerLevel, LoggerLevelNumbers } from "./modules/logger";
export type { Client, ClientProvider, ClientOptions, ClientConfigType } from "./modules/client";
export { ClientEvent, ConversationClientEvent } from "./modules/client";
export type { EventPublisher } from "./modules/events";
export type { TelemetryOptions } from "./modules/telemetry";

export type { VoiceCall, HoldCallOptions } from "~/modules/VoiceCall";

export { MessagesDirection } from "~/modules/Conversation";
export type {
    Conversation,
    SendTextMessageOptions,
    SendEmailMessageOptions,
    SendMediaMessageOptions
} from "~/modules/Conversation";
export type {
    ConversationsUser,
    UserUpdateReason,
    TwilioConversation,
    Message,
    MessageType,
    MessageUpdateReason,
    DeliveryStatus,
    Media,
    ConversationParticipant,
    ConversationsClient,
    MessageBuilder,
    Paginator,
    MediaCategory,
    ConversationParticipantType,
    ParticipantEmailLevel,
    ParticipantUpdateReason,
    ConversationStatus,
    ConversationUpdateReason,
    NotificationLevel
} from "~/modules/conversations";

export type { DataClientOptions, GetToken, TypePolicies } from "~/modules/dataClient";

export type { VoiceOptions } from "./modules/voice/voiceOptions";


export type { DeepPartial } from "./utils/DeepPartial";
export type { Without, XOR } from "./utils/utilityTypes";


export type { TaskRouterOptions } from "./packages/taskrouter/TaskRouterOptions";
export { Supervisor, Worker, Workspace } from "./packages/taskrouter";
export * from "./packages/taskrouter/types";


export type {
    AddVoiceParticipantResponse,
    AddVoiceParticipantOptions,
    AddVoiceParticipantMediaProperties,
    ParticipantResponse,
    TaskParticipant,
    Transfer,
    ParticipantCallProperties,
    ParticipantType,
    ParticipantCallStatusProperties,
    ParticipantCallRecordProperties,
    ParticipantSipProperties,
    MediaChannelType,
    RoutingProperties,
    MediaProperties,
    ParticipantEventData,
    RoutingData,
    PausedChannelsResponse
} from "./modules/cbm";


const ctx = ContextManager.newInstance();













export const auth = (accountSid: string) => createAuthenticator(ctx)(accountSid);















export const setLogLevel = (level: LoggerLevel) => fromModuleSetLogLevel(ctx)(level);



















export const getPublicConfig = (option: XOR<AccountSidOption, RuntimeDomainOption>) =>
    fromModuleGetPublicConfig(option);

















export const getAccountConfig = (sessionToken: string, options?: AccountConfigOptions) =>
    getAccountConfigData(sessionToken, options);








export const setAccountConfig = (accountSid: string, sessionToken: string, config: Partial<AccountConfigData>) =>
    setAccountConfigData(accountSid, sessionToken, config);



















export const getAuthenticationConfig = (config: AuthenticationConfigParams) =>
    fromModuleGetAuthenticationConfig(config);































export const getLoginDetails = (options: LoginDetailsParams) => fromModuleGetLoginDetails(options);

















export const validateToken = (accountSid: string, token: string) => fromModuleValidateToken(accountSid, token);
















export const refreshToken = (options: RefreshTokenParams): Promise<RefreshTokenResponse> =>
    fromModuleRefreshToken(options);





















export const exchangeToken = (options: ExchangeTokenParams): Promise<ExchangeTokenResponse> =>
    fromModuleExchangeToken(options);
















export const getFeaturesConfig = (token: string, uiVersion?: string) => fromModuleGetFeaturesConfig(token, uiVersion);





















export const createClient = (token: string, userOptions?: DeepPartial<ClientOptions>) =>
    fromModuleCreateClient(token, userOptions);






export const version: string = packageInfo.version;

export type { FlexSdkErrorMetadata } from "./modules/error";
export { FlexSdkError, ErrorCode, ErrorSeverity } from "./modules/error";
